import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { BarChart3, TrendingUp, Users, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ApiService from '../services/api';

interface PowerBIReport {
  id: number;
  nombre: string;
  descripcion: string;
  webUrl: string;
  embedUrl: string;
  datasetId: string;
  icono: string;
  categoria: string;
  activo: boolean;
  fechaCreacion: string;
  rolesPermitidos?: string[];
  orden: number;
}

interface PowerBIReportsProps {
  searchTerm: string;
}

const PowerBIReports: React.FC<PowerBIReportsProps> = ({ searchTerm }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState<PowerBIReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<PowerBIReport | null>(null);

  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cargar reportes desde la API
        const reportesData = await ApiService.getReportes(true); // Solo reportes activos
        
        // Filtrar reportes según los permisos del usuario (si están definidos)
        const userReports = reportesData.filter(report => {
          if (!report.rolesPermitidos || report.rolesPermitidos.length === 0) {
            return true; // Sin restricciones de roles
          }
          
          // Si hay roles permitidos, verificar si el usuario tiene alguno
          // Por simplicidad, usaremos el username o algún rol del usuario
          const userRole = user?.username?.includes('admin') ? 'Admin' : 'User';
          return report.rolesPermitidos.includes(userRole);
        });

        setReports(userReports);
      } catch (err) {
        setError('Error cargando reportes de Power BI');
        console.error('Error loading Power BI reports:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadReports();
    }
  }, [user]);

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'BarChart3': return BarChart3;
      case 'TrendingUp': return TrendingUp;
      case 'Users': return Users;
      case 'FileText': return FileText;
      default: return BarChart3;
    }
  };

  const getCategoryColor = (categoria: string) => {
    switch (categoria) {
      case 'Ejecutivo': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      case 'Recursos Humanos': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'Operaciones': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'Finanzas': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'General': return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700';
      default: return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700';
    }
  };

  const filteredReports = reports.filter(report =>
    report.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.categoria.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleReportClick = (report: PowerBIReport) => {
    // Abrir embebido en la misma aplicación
    setSelectedReport(report);
  };

  const handleOpenInPowerBI = (report: PowerBIReport) => {
    window.open(report.webUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
        <span className="ml-3 text-neutral-600 dark:text-neutral-400">Cargando reportes de Power BI...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg p-6 text-center">
        <div className="text-error mb-2">⚠️ Error</div>
        <p className="text-error">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 inline-flex items-center px-3 py-1 border border-error/30 rounded text-sm text-error bg-white dark:bg-neutral-900 hover:bg-error/5 dark:hover:bg-error/10"
        >
          <RefreshCw size={14} className="mr-1" />
          Reintentar
        </button>
      </div>
    );
  }

  // Vista del reporte seleccionado (simplificada)
  if (selectedReport) {
    return (
      <div className="space-y-4">
        {/* Header minimalista */}
        <div className="flex items-center justify-between card p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedReport(null)}
              className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 font-medium"
            >
              ← Volver
            </button>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">{selectedReport.nombre}</h2>
              <span className={`inline-block px-2 py-1 rounded-full text-xs border ${getCategoryColor(selectedReport.categoria)}`}>
                {selectedReport.categoria}
              </span>
            </div>
          </div>
          <button
            onClick={() => handleOpenInPowerBI(selectedReport)}
            className="btn-primary text-sm"
          >
            <ExternalLink size={16} className="mr-1" />
            Abrir en Power BI
          </button>
        </div>

        {/* Reporte embebido - pantalla completa */}
        <div className="card">
          <div className="aspect-video w-full">
            <iframe
              src={`${selectedReport.embedUrl}&chromeless=1&filter=usuario eq '${user?.username}'`}
              className="w-full h-full rounded-lg"
              frameBorder="0"
              allowFullScreen
              title={selectedReport.nombre}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Grid de reportes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {filteredReports.map(report => {
          const Icon = getIcon(report.icono);
          return (
            <div
              key={report.id}
              className="card p-6 cursor-pointer group"
              onClick={() => handleReportClick(report)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
                    <Icon className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 group-hover:text-primary transition-colors">
                      {report.nombre}
                    </h4>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs border ${getCategoryColor(report.categoria)}`}>
                      {report.categoria}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 line-clamp-3">
                {report.descripcion}
              </p>

              <div className="flex items-center justify-between">
                <button
                  className="text-primary hover:text-primary-dark text-sm font-medium flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReportClick(report);
                  }}
                >
                  Ver reporte →
                </button>
                <button
                  className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 p-1 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInPowerBI(report);
                  }}
                >
                  <ExternalLink size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-600 mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            No se encontraron reportes
          </h3>
          <p className="text-neutral-500 dark:text-neutral-400">
            {searchTerm ?
              `No hay reportes que coincidan con "${searchTerm}"` :
              'No tienes acceso a reportes en este momento'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default PowerBIReports;